/**
 * ScheduleView - List grouped by day buckets (Today, Tomorrow, This Week, Next Week, Later).
 *
 * An improved version of the bucket list approach, showing tasks in a simple list format
 * with date chips and subtle dependency hints.
 */

import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { Participant, PlanRole } from '../../../types/generated/graphql';
import { ScheduleResult, ScheduledStep } from '../../../utils/calendarScheduler';
import { colors } from '../../../theme/colors';
import TaskCard from './TaskCard';

type BucketType = 'today' | 'tomorrow' | 'this-week' | 'next-week' | 'later';

interface BucketGroup {
  bucket: BucketType;
  label: string;
  steps: ScheduledStep[];
}

interface ScheduleViewProps {
  scheduleResult: ScheduleResult;
  participants: Participant[];
  roles?: PlanRole[];
  currentParticipantId: string | null;
  onNavigateToTask: (taskId: string) => void;
}

/**
 * Get the start of today (midnight).
 */
function getToday(): Date {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
}

/**
 * Add days to a date.
 */
function addDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

/**
 * Get the end of the current week (Sunday midnight).
 */
function getEndOfWeek(today: Date): Date {
  const endOfWeek = new Date(today);
  const dayOfWeek = today.getDay();
  const daysUntilSunday = 7 - dayOfWeek;
  endOfWeek.setDate(today.getDate() + daysUntilSunday);
  endOfWeek.setHours(23, 59, 59, 999);
  return endOfWeek;
}

/**
 * Get the end of next week.
 */
function getEndOfNextWeek(today: Date): Date {
  const endOfNextWeek = getEndOfWeek(today);
  endOfNextWeek.setDate(endOfNextWeek.getDate() + 7);
  return endOfNextWeek;
}

/**
 * Determine which bucket a date falls into.
 */
function getBucketForDate(date: Date): BucketType {
  const today = getToday();
  const tomorrow = addDays(today, 1);
  const endOfWeek = getEndOfWeek(today);
  const endOfNextWeek = getEndOfNextWeek(today);

  const dateDay = new Date(date);
  dateDay.setHours(0, 0, 0, 0);

  if (dateDay.getTime() === today.getTime()) {
    return 'today';
  }

  if (dateDay.getTime() === tomorrow.getTime()) {
    return 'tomorrow';
  }

  if (dateDay <= endOfWeek) {
    return 'this-week';
  }

  if (dateDay <= endOfNextWeek) {
    return 'next-week';
  }

  return 'later';
}

/**
 * Group scheduled steps into buckets.
 */
function groupIntoBuckets(scheduleResult: ScheduleResult): BucketGroup[] {
  const bucketOrder: BucketType[] = ['today', 'tomorrow', 'this-week', 'next-week', 'later'];
  const bucketLabels: Record<BucketType, string> = {
    today: 'Today',
    tomorrow: 'Tomorrow',
    'this-week': 'This Week',
    'next-week': 'Next Week',
    later: 'Later',
  };

  // Collect all steps from the schedule
  const allSteps: ScheduledStep[] = [];
  for (const day of scheduleResult.days) {
    allSteps.push(...day.morningTasks);
    allSteps.push(...day.afternoonTasks);
    allSteps.push(...day.eveningTasks);
    allSteps.push(...day.allDayTasks);
  }

  // Group by bucket
  const bucketMap = new Map<BucketType, ScheduledStep[]>();
  for (const bucket of bucketOrder) {
    bucketMap.set(bucket, []);
  }

  for (const scheduledStep of allSteps) {
    const bucket = getBucketForDate(scheduledStep.suggestedDay);
    bucketMap.get(bucket)?.push(scheduledStep);
  }

  // Build result
  const groups: BucketGroup[] = [];
  for (const bucket of bucketOrder) {
    const steps = bucketMap.get(bucket) || [];
    if (steps.length > 0) {
      // Sort by date within bucket
      steps.sort((a, b) => a.suggestedDay.getTime() - b.suggestedDay.getTime());
      groups.push({
        bucket,
        label: bucketLabels[bucket],
        steps,
      });
    }
  }

  return groups;
}

/**
 * Get bucket header color.
 */
function getBucketColor(bucket: BucketType): string {
  switch (bucket) {
    case 'today':
      return colors.blue[600];
    case 'tomorrow':
      return colors.blue[500];
    case 'this-week':
      return colors.gray[600];
    case 'next-week':
      return colors.gray[500];
    case 'later':
      return colors.gray[400];
    default:
      return colors.gray[500];
  }
}

/**
 * Get assignee from participants (only if not current user).
 */
function getAssignee(
  step: ScheduledStep,
  participants: Participant[],
  currentParticipantId: string | null
): Participant | null {
  if (!step.step.assignedToId) return null;
  // Only show assignee if it's NOT the current user
  if (step.step.assignedToId === currentParticipantId) return null;
  return participants.find((p) => p.id === step.step.assignedToId) || null;
}

/**
 * Check if a task is assigned to someone else (waiting).
 */
function isWaitingTask(step: ScheduledStep, currentParticipantId: string | null): boolean {
  if (!currentParticipantId || !step.step.assignedToId) return false;
  return step.step.assignedToId !== currentParticipantId;
}

/**
 * Bucket section component.
 */
interface BucketSectionProps {
  group: BucketGroup;
  participants: Participant[];
  currentParticipantId: string | null;
  onNavigateToTask: (taskId: string) => void;
}

const BucketSection: React.FC<BucketSectionProps> = ({
  group,
  participants,
  currentParticipantId,
  onNavigateToTask,
}) => {
  return (
    <Box sx={{ mb: 3 }}>
      {/* Bucket header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 1.5,
          px: 0.5,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 600,
            color: getBucketColor(group.bucket),
            textTransform: 'uppercase',
            fontSize: '0.7rem',
            letterSpacing: 0.5,
          }}
        >
          {group.label}
        </Typography>
        <Typography variant="caption" sx={{ color: colors.gray[400] }}>
          {group.steps.length}
        </Typography>
      </Box>

      {/* Task cards */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {group.steps.map((scheduledStep) => (
          <TaskCard
            key={scheduledStep.step.id}
            scheduledStep={scheduledStep}
            variant="full"
            assignee={getAssignee(scheduledStep, participants, currentParticipantId)}
            isWaiting={isWaitingTask(scheduledStep, currentParticipantId)}
            onClick={() => onNavigateToTask(scheduledStep.step.id)}
          />
        ))}
      </Box>
    </Box>
  );
};

/**
 * Main ScheduleView component.
 */
export const ScheduleView: React.FC<ScheduleViewProps> = ({
  scheduleResult,
  participants,
  currentParticipantId,
  onNavigateToTask,
}) => {
  // Group steps into buckets
  const groups = useMemo(
    () => groupIntoBuckets(scheduleResult),
    [scheduleResult]
  );

  if (groups.length === 0) {
    return (
      <Box
        sx={{
          textAlign: 'center',
          py: 6,
          color: colors.gray[500],
        }}
      >
        <Typography variant="body2">No tasks scheduled</Typography>
        <Typography variant="caption" sx={{ color: colors.gray[400] }}>
          Tasks will appear here once scheduled
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {groups.map((group) => (
        <BucketSection
          key={group.bucket}
          group={group}
          participants={participants}
          currentParticipantId={currentParticipantId}
          onNavigateToTask={onNavigateToTask}
        />
      ))}
    </Box>
  );
};

export default ScheduleView;
